import { navigateTo, useFetch, useRoute, type UseFetchOptions } from "nuxt/app";
import { NUXT_API_URL } from "~/types/env";
import { userLogin } from "./useLogin";
import { matchneedLoginPath } from "~/utils/route";
import { useAccountStore, AccoutLocalStorage } from "~/store/account";
import { Cache } from "~/types/cache";
import { ArticleLocale } from "~/types/locale";
import { ErrorType, useSentry } from "~/composables/useSentry";

interface RequestOptions extends UseFetchOptions<any> {
  customBaseURL?: string;
  useRawUrl?: boolean;
  // 是否支持传入后台的token功能
  isEnableBackend?: boolean;
  // 是否禁用HTTP缓存
  disableCache?: boolean;
}
enum HttpMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
  PATCH = "PATCH",
}
type HandleRequestOptions = { request: Request; options: RequestOptions };
type UseFetchOnResponseOptions = {
  error: any;
  headers: any;
  options: any;
  response: any;
};
const REFLESH_TOKEN_API = "/api/v1/model-user-server/auth/refresh-token";
const LOGIN_EXPIRED_CODE = 401;

let isRefreshing = false; // 是否正在刷新 Token
let requests: any[] = []; // 等待队列

// 刷新 Token;
const refreshToken = async () => {
  try {
    const accountStore = useAccountStore();
    const response = await useFetch(REFLESH_TOKEN_API, {
      method: "POST",
      body: { refreshToken: accountStore?.refresh_token },
      baseURL: NUXT_API_URL,
    });
    console.log("refreshToken response:", response);
    if (response.data?.value.code === 0) {
      accountStore.setData(response.data?.value.data);
      return true;
    }
    return false;
  } catch (error) {
    console.error("useRequest ERROR -> refreshToken error:", error);
    return false;
  }
};

// 响应拦截器;
async function onResponse(e: UseFetchOnResponseOptions) {
  // 其他错误处理;
  if (e.response._data.error) {
    useToast.error(e.response._data.error?.message || "Response error");
    throw new Error(e.response._data.error?.message || "Response error");
  }
  // 成功响应直接返回数据;
  if (e.response._data.code === LOGIN_EXPIRED_CODE) {
    if (!isRefreshing) {
      isRefreshing = true;
      try {
        const refreshSuccess = await refreshToken();
        if (!refreshSuccess) {
          useAccountStore().doClearAccount();
          userLogin()
            .then(() => {
              navigateTo("/account/register");
            })
            .catch((e) => {
              console.log(e);
              if (e === "close") {
                const route = useRoute();
                if (route && matchneedLoginPath(route.name)) {
                  backToHome();
                }
              } else if (e === "cancel") {
                navigateTo("/account/login");
              }
            });
          // throw new Error("useRequest ERROR -> Token Relesh Failed");
          console.log("useRequest ERROR -> Token Relesh Failed");
          return e.response._data;
        }
        //#region backups2  执行等待队列中的请求(传递新Token)修复Token 刷新队列逻辑;
        const pendingRequests = [...requests];
        requests = [];
        pendingRequests.forEach((cb) =>
          cb(AccoutLocalStorage.value!.access_token)
        );
        return await retryOriginalRequest(e);
        // #endregion ;
        // #region backups1 执行等待队列中的请求(传递新Token);
        // requests.forEach((cb) => cb(AccoutLocalStorage.value!.access_token));
        // requests = [];
        // // 重试原始请求并返回结果;
        // const retryResponse = await useFetch(originalConfig.url, {
        //   ...originalConfig.options,
        //   headers: {
        //     ...originalConfig.headers,
        //     Authorization: `Bearer ${AccoutLocalStorage.value!.access_token}`,
        //   },
        // });
        // return retryResponse.data.value;
        // #endregion ;
      } finally {
        isRefreshing = false;
      }
    } else {
      return new Promise((resolve) => {
        requests.push((newToken: string) => {
          resolve(retryOriginalRequest(e, newToken));
        });
      });
      // #region backups 1 将重试请求加入队列，返回 Promise 等待;
      // return new Promise((resolve) => {
      //   requests.push((newToken: string) => {
      //     resolve(
      //       useFetch(originalConfig.url, {
      //         ...originalConfig.options,
      //         headers: {
      //           ...originalConfig.headers,
      //           Authorization: `Bearer ${newToken}`,
      //         },
      //       }).then(({ data }) => data.value)
      //     );
      //   });
      // });
      // #endregion ;
    }
  } else {
    return e.response._data;
  }
}

// 封装重试原始请求的逻辑;
async function retryOriginalRequest(
  e: UseFetchOnResponseOptions,
  newToken?: string
) {
  const originalConfig = {
    url: e.response.url,
    options: { ...e.options },
    headers: { ...e.headers },
  };
  if (newToken) {
    originalConfig.headers.Authorization = `Bearer ${newToken}`;
  }
  const { data } = await useFetch(originalConfig.url, originalConfig.options);
  return data.value;
}

// 请求拦截器;
function onRequest({ options }: HandleRequestOptions) {
  const { access_token } = useAccountStore();
  if (!!access_token) {
    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${access_token}`,
    };
  }
  // 支持后台预览待审批的模型需求 在url中获取后台token数据;
  if (options.isEnableBackend) {
    const route = useRoute();
    if (route.query.token) {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${route.query.token}`,
      };
    }
  }
  const language = useCookie(Cache.LOCALE).value;
  if (language) {
    // 支持请求中携带语言信息,格式为standardLocale(标准语言格式)
    options.headers = {
      ...options.headers,
      "User-Lang": ArticleLocale[language as "zh"],
    };
  }
}

const getFetchParms = (param) => {
  // 适配GET | DELETE请求;
  if (
    param &&
    (param.method === HttpMethod.GET || param.method === HttpMethod.DELETE)
  ) {
    const query = param.body;
    delete param.body;
    return { ...param, query };
  }
  // 这里是为了nuxt的useFetch在url相同的情况下（特别是开了SSR的情况下），网络请求的缓存问题;
  if (param && param.body && param.body.path && param.disableCache) {
    param.key = param.body.path;
  }
  return param;
};

// 修改后的请求创建方法
function createUseFetchRequest(method: HttpMethod) {
  return function (url: string, data?: any, options: RequestOptions = {}) {
    const requestUrl = options.useRawUrl ? url : NUXT_API_URL + url;
    return new Promise((resolve, reject) => {
      useFetch(requestUrl, {
        ...getFetchParms({ method, body: data }),
        ...options,
        onRequest,
        async onResponse(context) {
          try {
            resolve(await onResponse(context));
          } catch (error) {
            networkErrorHandler(error, requestUrl);
            reject(error);
          }
        },
        async onRequestError(context) {
          networkErrorHandler(context.error, requestUrl);
          reject(context.error || "Request:Failed to fetch");
        },
        async onResponseError(context) {
          networkErrorHandler(context.error, requestUrl);
          reject(context.error || "Request:Failed to fetch");
        },
      });
    });
  };
}

// 网络错误的上报
const networkErrorHandler = (error: any, url: string) => {
  useSentry(
    Error(`useRequest Error: ${error}, url: ${url}`),
    ErrorType.Network
  );
};

export const useGet = createUseFetchRequest(HttpMethod.GET);
export const usePost = createUseFetchRequest(HttpMethod.POST);
export const usePut = createUseFetchRequest(HttpMethod.PUT);
export const useDelete = createUseFetchRequest(HttpMethod.DELETE);
export const usePatch = createUseFetchRequest(HttpMethod.PATCH);
